import { Task, TaskStatus } from "@/graphql/__generated__/graphql";
import { Columns } from "./interfaces";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { Dispatch, SetStateAction } from "react";

/**
 * Function to find the column key for a given task ID.
 * Column Keys are the TaskStatus enum values.
 * @param taskId - ID of the task to find
 * @param columns - Columns object to search
 * @returns Column Key - TaskStatus enum value or undefined
 */
export function findColumnKeyForTask(
  taskId: number,
  columns: Columns
): TaskStatus | undefined {
  return Object.entries(columns).find(([, tasks]) =>
    tasks.some((task: Task) => task.id === taskId)
  )?.[0] as TaskStatus | undefined;
}

/**
 * Function to handle the drag start event.
 * @param event - DragStartEvent
 * @param columns - An object of columns
 * @param setActiveTask - Function to set the active task
 */
export function handleDragStart(
  event: DragStartEvent,
  columns: Columns,
  setActiveTask: Dispatch<SetStateAction<Task | null>>
): void {
  const taskId = Number(event.active.id);

  const foundTask = Object.values(columns)
    .flat()
    .find((t) => t.id === taskId);

  setActiveTask(foundTask || null);
}

/**
 * Function to handle the drag end event.
 * Used to handle the reordering/moving of tasks between columns,
 * and updates the tasks in the database.
 * @param event - DragEndEvent
 * @param columns - An object of columns
 * @param setActiveTask - Function to set the active task
 * @param setColumns - Function to set the columns
 * @param updateTasksMutation - Function to update the tasks in the database
 * @returns void
 */
export function handleDragEnd(
  event: DragEndEvent,
  columns: Columns,
  setActiveTask: Dispatch<SetStateAction<Task | null>>,
  setColumns: Dispatch<SetStateAction<Columns>>,
  updateTasksMutation: any
): void {
  const { active, over } = event;
  setActiveTask(null);

  // List of columns keys that are valid for dropping
  const possibleColumnKeys = [TaskStatus.Todo, TaskStatus.InProgress, TaskStatus.Done];

  if (!over) {
    // Dropped outside any column or droppable zones
    return;
  }

  // Convert the dragged item ID into a number
  const draggedTaskId = Number(active.id);

  // Exit when drag on itself
  if (draggedTaskId === over.id) return;

  // Source column is whatever column currently holds the dragged task
  const sourceColumnKey = findColumnKeyForTask(draggedTaskId, columns);

  // If we can't find where the dragged task came from, return
  if (!sourceColumnKey) return;

  let destinationColumnKey: string | TaskStatus | undefined;
  // overTask is the task that the dragged task is being dropped on top of
  let overTaskId = 0; // 0 || undefined to place at the end of the column

  // Determine if the drop target is a column or a task
  if (possibleColumnKeys.includes(over.id as TaskStatus)) {
    // Dropped onto an empty column
    destinationColumnKey = over.id as TaskStatus;
  } else {
    // Dragged onto a task
    overTaskId = Number(over.id);

    // Find which column the overTask belongs to
    destinationColumnKey = findColumnKeyForTask(overTaskId, columns);
  }

  if (!destinationColumnKey) return;

  // Keep track of old columns and make a deep clone of the columns so we can safely mutate it
  const oldColumns = columns;
  let newColumns = {
    [TaskStatus.Todo]: [...oldColumns.TODO],
    [TaskStatus.InProgress]: [...oldColumns.IN_PROGRESS],
    [TaskStatus.Done]: [...oldColumns.DONE],
  };

  if (sourceColumnKey === destinationColumnKey) {
    // Reordering within the same column
    newColumns = reorderWithinColumn(
      newColumns,
      sourceColumnKey,
      draggedTaskId,
      overTaskId
    );
  } else {
    // Moving to a different column
    newColumns = moveBetweenColumns(
      newColumns,
      sourceColumnKey,
      destinationColumnKey as TaskStatus,
      draggedTaskId,
      overTaskId
    );
  }

  // Update local state with new reordered columns
  setColumns(newColumns);

  // get changed tasks from source & destination columns
  // changes only in the source column
  const changedInSource = newColumns[sourceColumnKey].map((task) => ({
    id: task.id,
    order: task.order,
    status: sourceColumnKey,
  }));

  // changes across columns
  let changedInDestination: Partial<Task>[] = [];
  if (sourceColumnKey !== destinationColumnKey) {
    changedInDestination = newColumns[destinationColumnKey].map((task) => ({
      id: task.id,
      order: task.order,
      status: destinationColumnKey,
    }));
  }

  // Combine all changed tasks
  const changedTasks = [...changedInSource, ...changedInDestination].map((task) => ({
    ...task,
    id: task.id as number,
  }));

  updateTasksMutation({
    variables: { input: changedTasks },
  });
}

/**
 * Helper function to move a task between columns
 * @param columns - list of columns to be edited
 * @param sourceColumnKey - source column key
 * @param destinationColumnKey - destination column key
 * @param draggedTaskId - task id being dragged
 * @param overTaskId - task id being dragged over
 * @returns - updated columns
 */
export function moveBetweenColumns(
  columns: Columns,
  sourceColumnKey: TaskStatus,
  destinationColumnKey: TaskStatus,
  draggedTaskId: number,
  overTaskId: number
): Columns {
  // Copy and sort source/destination columns by order
  const sourceTasks = [...columns[sourceColumnKey]].sort((a, b) => a.order - b.order);
  const destTasks = [...columns[destinationColumnKey]].sort((a, b) => a.order - b.order);

  // Find the dragged task in the source
  const indexOfTask = sourceTasks.findIndex((t) => t.id === draggedTaskId);
  if (indexOfTask === -1) return columns;

  // Remove dragged task from source
  const [movedTask] = sourceTasks.splice(indexOfTask, 1);

  // Insert into destination based on overTaskId
  if (overTaskId) {
    // Find position of overTask in the destination
    const overIndex = destTasks.findIndex((t) => t.id === overTaskId);
    if (overIndex === -1) {
      // If overTask isn't found, push to the end
      destTasks.push(movedTask);
    } else {
      // Insert the dragged task "above" the overIndex
      destTasks.splice(overIndex, 0, movedTask);
    }
  } else {
    // If there's no overTaskId, just push at the end
    destTasks.push(movedTask);
  }

  // Reassign order in each list so it's sequential
  sourceTasks.forEach((task, i) => {
    task.order = i;
  });
  destTasks.forEach((task, i) => {
    task.order = i;
  });

  return {
    ...columns,
    [sourceColumnKey]: sourceTasks,
    [destinationColumnKey]: destTasks,
  };
}

/**
 * Helper function to reorder tasks within a column
 * @param columns - list of columns to be edited
 * @param columnKey - column key
 * @param draggedTaskId - task id being dragged
 * @param overTaskId - task id being dragged over
 * @returns - updated columns
 */
export function reorderWithinColumn(
  columns: Columns,
  columnKey: TaskStatus,
  draggedTaskId: number,
  overTaskId: number
) {
  // Copy and sort the column by order
  const colTasks = [...columns[columnKey]];
  colTasks.sort((a, b) => a.order - b.order);

  // Find the dragged task and over task
  const oldIndex = colTasks.findIndex((t) => t.id === draggedTaskId);
  const newIndex = colTasks.findIndex((t) => t.id === overTaskId);

  // If either index is invalid, or no actual change in position, return existing columns
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return columns;
  }

  // Reorder the tasks
  const reordered = arrayMove(colTasks, oldIndex, newIndex);

  // Reassign order so it's sequential
  reordered.forEach((task, i) => {
    task.order = i;
  });

  return {
    ...columns,
    [columnKey]: reordered,
  };
}
